export const SHX_STATUSES = [
  { id: "completed", label: "Completed" },
  { id: "in-progress", label: "In progress" },
  { id: "not-done", label: "Not done" },
  { id: "unknown", label: "Unknown" },
];

export const SHX_LATERALITY = [
  { id: "na", label: "N/A" },
  { id: "left", label: "Left" },
  { id: "right", label: "Right" },
  { id: "bilateral", label: "Bilateral" },
];

export const SHX_SOURCES = [
  { id: "clinician", label: "Clinician Entry" },
  { id: "patient", label: "Patient Report" },
  { id: "imported", label: "Imported" },
  { id: "record", label: "Record" },
];

export const SHX_SORT = [
  { id: "relevance", label: "Relevance" },
  { id: "name", label: "Name" },
  { id: "code", label: "Px Code" },
];

export const SHX_VALUE_SETS = [
  { id: "surgical-history", label: "Surgical History" },
  { id: "procedures", label: "Procedures" },
  { id: "orders", label: "Orders" },
  { id: "billing", label: "Billing" },
  { id: "", label: "All Procedures" },
];

const LOCAL_PX_HINT = "emrbymd.com/fhir/CodeSystem/px";

export function statusLabel(status) {
  return SHX_STATUSES.find((row) => row.id === status)?.label || status || "Completed";
}

export function lateralityLabel(code, laterality) {
  if (laterality?.text) return laterality.text;
  const key = (code || "na").toLowerCase();
  return SHX_LATERALITY.find((row) => row.id === key)?.label || "N/A";
}

export function sourceLabel(source) {
  if (!source) return "Clinician Entry";
  if (typeof source === "string") {
    return SHX_SOURCES.find((row) => row.id === source)?.label || source;
  }
  if (source.display) return source.display;
  return SHX_SOURCES.find((row) => row.id === source.kind)?.label || "Clinician Entry";
}

export function formatPxDisplay(item) {
  const code = item?.localCode || item?.coding?.code || "";
  const description = item?.display || item?.shortDisplay || item?.chip || "";
  if (code && description) return `${description} (${code})`;
  return description || code || "—";
}

export function formatSearchPx(item) {
  const code = item?.localCode || "";
  const description = item?.display || item?.shortDisplay || "";
  if (code && description) return `${code} — ${description}`;
  return description || code;
}

export function toSurgicalHistoryPayload(item, extras = {}) {
  return {
    pxConceptId: item.pxConceptId || item.id,
    performedDate: extras.performedDate || null,
    status: extras.status || "completed",
    comment: extras.comment || null,
    laterality: extras.laterality || "na",
    performerDisplay: extras.performerDisplay || null,
    locationDisplay: extras.locationDisplay || null,
    source: extras.source || "clinician",
    reviewed: !!extras.reviewed,
  };
}

export function formatReviewedAt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function isoToManualDate(iso) {
  if (!iso) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match) return `${match[2]}/${match[3]}/${match[1]}`;
  try {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return "";
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${month}/${day}/${parsed.getFullYear()}`;
  } catch {
    return "";
  }
}

export function manualDateToIso(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  const match = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/.exec(text);
  if (!match) return "";
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function todayIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export function mappingSystemLabel(system) {
  const url = String(system || "");
  if (url.includes("snomed.info/sct")) return "SNOMED CT";
  if (url.includes("ama-assn.org/go/cpt")) return "CPT";
  if (url.includes("Medicare/Coding/ICD10") || url.includes("icd-10-pcs") || url.includes("ICD10")) {
    return "ICD-10-PCS";
  }
  if (url.includes("HCPCS")) return "HCPCS";
  if (url.includes(LOCAL_PX_HINT)) return "Px";
  return "Code";
}

export function standardMappings(concept) {
  const seen = new Set();
  const rows = [];
  const candidates = [
    ...(concept?.mappings || []),
    ...((concept?.codeableConcept?.coding || []).map((coding) => ({
      system: coding.system,
      code: coding.code,
      display: coding.display,
    })) || []),
  ];
  for (const row of candidates) {
    const system = row?.system || "";
    const code = (row?.code || "").trim();
    if (!code || system.includes(LOCAL_PX_HINT)) continue;
    const key = `${system}|${code}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({
      system,
      code,
      display: row.display,
      label: mappingSystemLabel(system),
    });
  }
  return rows;
}

export function hasBodySiteField() {
  return false;
}
